import React, { useEffect, useMemo, useState } from "react";

import { ZonedDateTime } from "@js-joda/core";
import {
  useDeleteObjectDatasetDataFile,
  useGetObjectDatasetDataPagedInRange,
} from "api/api-hooks/object-dataset-hooks";
import DataTable from "components/basic/data-table/DataTable";
import DateTimeRange from "components/basic/date-time-range/DateTimeRange";
import { DatasetLoadingConf } from "components/dashboard/charts/interfaces";
import { FileInfo } from "components/dashboard/product-template-table/utils/interfaces";
import { TableColumn } from "components/pages/measurement-devices/utils/types";
import useCurrentCompanyContext from "hooks/useCurrentCompanyContext";
import { useDisplayTimezone } from "hooks/useTimezone";
import { SortOrder } from "primereact/api";
import { Button } from "primereact/button";
import { DataTableStateEvent } from "primereact/datatable";
import { T } from "services/lang/LanguageService";
import { addToDate, fmtDatetime, toChronoUnit, toInstant } from "utils/time-utils";
import { confirmPopup } from "primereact/confirmpopup";
import { ObjectDatasetDataDto, ObjectDatasetWithDaysDto } from "../object-dataset-types";
import { getFileUrl } from "../utils/utils";

type ObjectDatasetGeneralProps = {
  data: ObjectDatasetWithDaysDto;
  loadConf: DatasetLoadingConf | undefined;
  rows?: number | null;
  showDelete?: boolean;
};

const ROWS = 10;

const ObjectDatasetGeneral: React.FC<ObjectDatasetGeneralProps> = ({
  data,
  loadConf,
  rows,
  showDelete = false,
}) => {
  const displayTz = useDisplayTimezone();
  const company = useCurrentCompanyContext();
  const [pagination, setPagination] = useState({ first: 0, rows: rows ?? ROWS });

  const initFrom = useMemo(() => {
    const lag = loadConf?.lag ?? 1;
    const lagUnit = loadConf?.lagUnit ?? "MONTHS";
    const alignUnit = loadConf?.alignUnit ?? "MINUTES";
    return addToDate(ZonedDateTime.now(displayTz).truncatedTo(toChronoUnit(alignUnit)), -lag, lagUnit);
  }, [displayTz, loadConf?.alignUnit, loadConf?.lag, loadConf?.lagUnit]);

  const initTo = useMemo(() => {
    const lead = loadConf?.lead ?? 1;
    const leadUnit = loadConf?.leadUnit ?? "MONTHS";
    const alignUnit = loadConf?.alignUnit ?? "MINUTES";
    return addToDate(ZonedDateTime.now(displayTz).truncatedTo(toChronoUnit(alignUnit)), lead, leadUnit);
  }, [displayTz, loadConf?.alignUnit, loadConf?.lead, loadConf?.leadUnit]);

  const [from, setFrom] = useState(initFrom);
  const [to, setTo] = useState(initTo);

  useEffect(() => {
    setFrom(initFrom);
  }, [initFrom]);

  useEffect(() => {
    setTo(initTo);
  }, [initTo]);

  const { data: objectDatasetData, isLoading } = useGetObjectDatasetDataPagedInRange(
    {
      identifier: data.identifier ?? "",
      companyRef: company?.companyReference ?? "",
      startDate: toInstant(from),
      endDate: toInstant(to),
      skip: pagination.first,
      take: pagination.rows,
    },
    {
      enabled: !!company?.companyReference && !!data.identifier && !!from && !!to,
      retry: false,
      refetchOnWindowFocus: false,
    }
  );

  const renderFileTable = (rowData: ObjectDatasetDataDto) => (
    <FileTable objectData={rowData} showDelete={showDelete} />
  );

  const columns: TableColumn<ObjectDatasetDataDto>[] = [
    {
      header: T("word.date"),
      field: "timestamp",
      columnKey: "timestamp",
      body: rowData => rowData.timestamp && fmtDatetime(rowData.timestamp, displayTz),
      sortable: true,
    },
    {
      header: T("word.updated-at"),
      field: "updatedAt",
      columnKey: "updatedAt",
      body: rowData => rowData.updatedAt && fmtDatetime(rowData.updatedAt, displayTz),
      sortable: true,
    },
    {
      header: T("word.file"),
      field: "fileInfos",
      columnKey: "fileInfos",
      body: renderFileTable,
    },
  ];

  const onPage = (e: DataTableStateEvent) => {
    setPagination({ first: e.first, rows: e.rows });
  };

  const rowsPerPageOptions = useMemo(
    () => [...new Set([5, 10, 25, 50, rows ?? 10])].sort((a, b) => a - b),
    [rows]
  );

  return (
    <>
      <div className="flex gap-2 mb-3">
        <DateTimeRange dateFrom={from} dateTo={to} selectDateFrom={setFrom} selectDateTo={setTo} />
      </div>
      <DataTable
        columns={columns}
        data={objectDatasetData?.items ?? []}
        tableConfig={{
          rows: pagination.rows,
          sortField: "timestamp",
          sortOrder: SortOrder.DESC,
          rowsPerPageOptions,
          loading: isLoading,
          lazy: true,
          groupRowsBy: "timestamp",
          totalRecords: objectDatasetData?.pageInfo.total ?? 0,
          first: pagination.first,
          onPage,
        }}
      />
    </>
  );
};

export default ObjectDatasetGeneral;

const FileTable: React.FC<{ objectData: ObjectDatasetDataDto; showDelete: boolean }> = ({
  objectData,
  showDelete,
}) => {
  const company = useCurrentCompanyContext();

  const downloadButton = (rowData: FileInfo) => (
    <Button
      type="button"
      onClick={() => {
        window.open(
          `${getFileUrl(company?.companyReference, objectData.identifier, objectData.timestamp, rowData.key)}?dl=true`
        );
      }}
      icon="pi pi-download"
      className="component-editor-button icon-green"
    />
  );

  const deleteButton = (rowData: FileInfo) => <DeleteFileButton rowData={objectData} fileKey={rowData.key} />;

  const columns: TableColumn<FileInfo>[] = [
    {
      header: T("word.file"),
      field: "fileName",
      columnKey: "fileName",
    },
    {
      header: T("action.download"),
      columnKey: "download",
      className: "w-3rem",
      body: downloadButton,
    },
    {
      header: T("action.delete"),
      columnKey: "delete",
      className: "w-3rem",
      body: deleteButton,
      hidden: !showDelete,
    },
  ];

  return (
    <DataTable
      columns={columns}
      data={objectData.fileInfos ?? []}
      tableConfig={{
        showHeaders: false,
        paginator: false,
      }}
    />
  );
};

const DeleteFileButton: React.FC<{ rowData: ObjectDatasetDataDto; fileKey: string }> = ({
  rowData,
  fileKey,
}) => {
  const company = useCurrentCompanyContext();
  const { mutate: deleteObjectDatasetDataFile, isPending } = useDeleteObjectDatasetDataFile();

  const handleDelete = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    confirmPopup({
      target: e.currentTarget,
      message: T("alert.are-you-sure-you-want-to-proceed"),
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: () => {
        deleteObjectDatasetDataFile({
          companyRef: company?.companyReference!,
          identifier: rowData.identifier,
          timestamp: rowData.timestamp,
          filePath: fileKey,
        });
      },
    });
  };

  return (
    <Button
      type="button"
      onClick={handleDelete}
      icon="pi pi-trash"
      loading={isPending}
      className="component-editor-button icon-red"
    />
  );
};
